import {
    TFormalOffer,
    TFormalOffers
} from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOfferInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOfferInterestOnlyPeriod';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { Pill, Radio } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

type TOfferChoiceButtonsProps = {
    formalOffers: TFormalOffers;
    selectedOfferId?: string;
    drawdownAmount: number;
    updateSelectedOffer: (offer: TFormalOffer) => void;
};
type TOfferChoiceButtonProps = {
    offer: TFormalOffer;
    isSelected: boolean;
    disabled: boolean;
    onSelect: () => void;
};

export const OfferChoiceButtons = ({
    formalOffers,
    selectedOfferId,
    drawdownAmount,
    updateSelectedOffer
}: TOfferChoiceButtonsProps) => {
    return (
        <div>
            <Text className="mt-xl font-med">
                <FormattedMessage
                    id="_.offer_choice_buttons.offer_select_title"
                    defaultMessage="How would you like to repay?"
                />
            </Text>
            <Text className="text-s text-structure-40">
                <FormattedMessage
                    id="_.offer_choice_buttons.offer_select_subHeading"
                    defaultMessage="You can make early repayments with no fees at any time."
                />
            </Text>

            <Radio.Group name="offer">
                {formalOffers.map((offer) => (
                    <OfferChoiceButton
                        key={offer.offer_id}
                        offer={offer}
                        isSelected={selectedOfferId === offer.offer_id}
                        disabled={drawdownAmount > offer.available_cash!}
                        onSelect={() => updateSelectedOffer(offer)}
                    />
                ))}
            </Radio.Group>
        </div>
    );
};

const OfferChoiceButton = ({
    offer,
    isSelected,
    disabled,
    onSelect
}: TOfferChoiceButtonProps) => {
    const isInterestOnlyOffer = getIsInterestOnlyOffer(offer);
    const interestOnlyPeriod = getFormalOfferInterestOnlyPeriod(offer)!;

    return (
        <Radio.Root className="mt-m p-0" variant="frame">
            <Radio.Label className="flex justify-between p-m">
                <div>
                    <Text className="font-med">
                        {isInterestOnlyOffer ? (
                            <FormattedMessage
                                id="_.interest_only.radio_buttons.interest_only_option"
                                defaultMessage="{loanDuration} monthly repayments, including {interestOnlyPeriod} months of only repaying interest"
                                values={{
                                    loanDuration: getDurationInMonths(
                                        offer.duration!
                                    ),
                                    interestOnlyPeriod
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="_.duration_choice.radio_buttons.monthly_repayments_option"
                                defaultMessage="{months} monthly repayments"
                                values={{
                                    months: getDurationInMonths(offer.duration!)
                                }}
                            />
                        )}
                    </Text>
                    <Text className="text-s text-structure-40">
                        {offer.withdrawal_fee_percentage! > 0 ? (
                            <FormattedMessage
                                id="_.offer_choice_buttons.offer_subHeading"
                                defaultMessage="Small upfront fee. Spread the cost with lower minimum monthly repayments."
                            />
                        ) : (
                            <FormattedMessage
                                id="_.offer_choice_buttons.offer_subHeading_no_fees"
                                defaultMessage="No upfront fee. Pay it off faster with higher minimum monthly repayments."
                            />
                        )}
                    </Text>

                    <div className="flex flex-wrap gap-s pt-s">
                        {isInterestOnlyOffer && (
                            <Pill>
                                <FormattedMessage
                                    id="_.interest_only.interest_only_available"
                                    defaultMessage="{interestOnlyPayments} month lower repayment period available"
                                    values={{
                                        interestOnlyPayments: interestOnlyPeriod
                                    }}
                                />
                            </Pill>
                        )}

                        {offer.personal_guarantee_proportion === 0 && (
                            <Pill variant="green">
                                <FormattedMessage
                                    id="_.duration_choice.radio_buttons.no_personal_guarantee_required"
                                    defaultMessage="No Personal Guarantee required"
                                />
                            </Pill>
                        )}
                    </div>
                </div>
                <Radio.Input
                    checked={isSelected}
                    disabled={disabled}
                    onChange={onSelect}
                />
            </Radio.Label>
        </Radio.Root>
    );
};
